import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ArrowDown, ArrowUp, ChevronDown, ChevronUp, Crosshair, GripVertical, MoreVertical } from 'lucide-react';
import { DndContext, KeyboardSensor, PointerSensor, closestCenter, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import apiClient from '../services/apiClient';
import VocabularyDirectionMenuItems, { vocabularyDirectionLabel } from './VocabularyDirectionMenuItems';
import { t } from '../i18n';
import defaultNoteCover from '../assets/note-covers/default-note-cover.svg';
import climbingPlayer from '../assets/vocabulary/beanstalk/protagonist-climb.webp';
import emptySproutScene from '../assets/vocabulary/beanstalk/empty-sprout-friends.webp';
import meadowLookout from '../assets/vocabulary/beanstalk/ethan-lotus-lookout.webp';
import rootTile from '../assets/vocabulary/beanstalk/root.webp';
import stemTile from '../assets/vocabulary/beanstalk/stem.webp';
import branchTile from '../assets/vocabulary/beanstalk/branch-right.webp';
import topTile from '../assets/vocabulary/beanstalk/top.webp';
import crownStar from '../assets/vocabulary/beanstalk/treetop-star.webp';
import { BEANSTALK_PADDING, BEANSTALK_ROW_HEIGHT, BEANSTALK_TILE_SIZE, BEANSTALK_VIRTUAL_THRESHOLD, getBeanstalkPosition, getBeanstalkRows, getBeanstalkSection, getVisibleBeanstalkRows, vocabularyNoteTitle } from '../utils/vocabularyBeanstalk';
import './VocabularyBeanstalk.css';

const v = (key, values) => t(`vocabulary.${key}`, undefined, values);
const focusable = element => Array.from(element?.querySelectorAll('button:not(:disabled), [href], input:not(:disabled)') || []);
const reducedMotion = () => Boolean(window.matchMedia?.('(prefers-reduced-motion: reduce)').matches);

const tileSources = { root: rootTile, stem: stemTile, branch: branchTile, top: topTile };
const tileShifts = { root: 0, stem: 0, branch: 1, top: -3 };

function BeanstalkTile({ kind, top }) {
  return <img className="vocabulary-beanstalk-tile" data-testid="vocabulary-beanstalk-tile" data-tile-kind={kind}
    src={tileSources[kind]} alt="" aria-hidden="true" width={512} height={512} decoding="async" draggable={false}
    style={{ top, marginLeft: tileShifts[kind] * BEANSTALK_TILE_SIZE / 512 }} />;
}

function BeanstalkCrownStar() {
  const starRef = useRef(null);
  const [shimmering, setShimmering] = useState(false);
  useEffect(() => {
    // A static star remains visible on browsers without viewport observation.
    if (typeof IntersectionObserver === 'undefined') return undefined;
    let inView = false;
    const syncVisibility = () => setShimmering(inView && !document.hidden);
    const observer = new IntersectionObserver(entries => {
      inView = entries.some(entry => entry.isIntersecting);
      syncVisibility();
    });
    observer.observe(starRef.current);
    document.addEventListener('visibilitychange', syncVisibility);
    return () => { observer.disconnect(); document.removeEventListener('visibilitychange', syncVisibility); };
  }, []);
  return <span ref={starRef} className={`vocabulary-beanstalk-crown-star${shimmering ? ' is-shimmering' : ''}`} data-testid="vocabulary-beanstalk-crown-star" aria-hidden="true">
    <img src={crownStar} alt="" width={40} height={40} decoding="async" draggable={false} />
  </span>;
}

function BeanstalkArtwork({ rows, height, hasRoot, hasTop }) {
  return <div className="vocabulary-beanstalk-artwork" aria-hidden="true">
    <BeanstalkTile kind={hasTop ? 'top' : 'stem'} top={0} />
    {rows.map(row => <React.Fragment key={row.id}>
      <BeanstalkTile kind="branch" top={row.top} />
      <BeanstalkTile kind="stem" top={row.top + BEANSTALK_TILE_SIZE} />
    </React.Fragment>)}
    <BeanstalkTile kind={hasRoot ? 'root' : 'stem'} top={height - BEANSTALK_PADDING} />
    {hasRoot && <img className="vocabulary-beanstalk-lookout" data-testid="vocabulary-beanstalk-lookout"
      src={meadowLookout} alt="" aria-hidden="true" width={512} height={512} loading="lazy" decoding="async" draggable={false} />}
  </div>;
}

export function VocabularySproutScene() {
  return <img className="vocabulary-sprout" data-testid="vocabulary-sprout" src={emptySproutScene}
    alt={v('sproutLabel')} width={1200} height={800} decoding="async" draggable={false} />;
}

function BookLeaf({ row, total, busy, moving, compact, virtualCompact, isMatch, onOpen, onEdit, onToggleEnabled, onSetDirection, onDelete, onMoveMode, onMove, registerControl, onNavigate }) {
  const { note, id, index, count } = row;
  const title = vocabularyNoteTitle(note);
  const [cover, setCover] = useState(null);
  const [nearViewport, setNearViewport] = useState(typeof IntersectionObserver === 'undefined');
  const [menuOpen, setMenuOpen] = useState(false);
  const leafRef = useRef(null);
  const optionsRef = useRef(null);
  const menuRef = useRef(null);
  const triggerRef = useRef(null);
  const menuId = React.useId();
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id, disabled: !moving || busy });
  useEffect(() => {
    if (!note.coverUrl || nearViewport) return undefined;
    const observer = new IntersectionObserver(entries => {
      if (entries.some(entry => entry.isIntersecting)) { setNearViewport(true); observer.disconnect(); }
    }, { rootMargin: '320px 0px' });
    if (leafRef.current) observer.observe(leafRef.current);
    return () => observer.disconnect();
  }, [note.coverUrl, nearViewport]);
  useEffect(() => {
    setCover(null);
    if (!note.coverUrl || !nearViewport) return undefined;
    let alive = true;
    let objectUrl;
    const controller = new AbortController();
    apiClient.fetchVocabularyCover(id, { signal: controller.signal }).then(blob => {
      if (alive) { objectUrl = URL.createObjectURL(blob); setCover(objectUrl); }
    }).catch(() => { /* The default cover remains available if a protected cover cannot load. */ });
    return () => { alive = false; controller.abort(); if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [id, note.coverUrl, nearViewport]);
  useEffect(() => {
    if (!menuOpen) return undefined;
    focusable(menuRef.current)[0]?.focus();
    const dismiss = event => { if (!optionsRef.current?.contains(event.target)) setMenuOpen(false); };
    document.addEventListener('pointerdown', dismiss, true);
    return () => document.removeEventListener('pointerdown', dismiss, true);
  }, [menuOpen]);
  const closeMenu = () => { setMenuOpen(false); triggerRef.current?.focus(); };
  const act = action => { closeMenu(); action?.(id); };
  const menuKeys = event => {
    if (event.key === 'Escape') { event.preventDefault(); event.stopPropagation(); closeMenu(); return; }
    if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return;
    event.preventDefault(); event.stopPropagation();
    const buttons = focusable(menuRef.current);
    const current = buttons.indexOf(document.activeElement);
    const next = event.key === 'Home' ? 0 : event.key === 'End' ? buttons.length - 1
      : (current + (event.key === 'ArrowDown' ? 1 : -1) + buttons.length) % buttons.length;
    buttons[next]?.focus();
  };
  const terminal = (Number(note.completedCount) || 0) + (Number(note.alreadyKnownCount) || 0) + (Number(note.canceledCount) || 0);
  const eligible = Number(note.eligibleCount) || 0;
  const pending = Number.isFinite(Number(note.pendingCount)) ? Number(note.pendingCount) : Math.max(0, count - terminal);
  return <li ref={element => { setNodeRef(element); leafRef.current = element; }} data-note-id={id} data-testid="vocabulary-map-book"
    className={`vocabulary-beanstalk-book${compact ? ' is-compact' : ''}${virtualCompact ? ' is-virtual-compact' : ''}${isMatch ? ' is-match' : ''}${note.isEnabled === false ? ' is-disabled' : ''}${isDragging ? ' is-dragging' : ''}${menuOpen ? ' has-menu' : ''}`}
    style={{ ...(compact && !virtualCompact ? {} : { top: row.top }), transform: CSS.Transform.toString(transform), transition }}>
    <div className="vocabulary-beanstalk-book-content">
      <div className="vocabulary-beanstalk-leaf">
        <button type="button" className="vocabulary-beanstalk-open" disabled={busy} aria-label={v('openBook', { title })} aria-describedby={`${menuId}-counts ${menuId}-direction`} onClick={() => onOpen(id)}
          onKeyDown={event => { if (['ArrowDown', 'ArrowUp'].includes(event.key)) { event.preventDefault(); onNavigate(id, event.key === 'ArrowDown' ? 1 : -1); } }}>
          <img src={cover || defaultNoteCover} alt="" loading="lazy" decoding="async" onError={() => setCover(null)} />
          <span className="vocabulary-beanstalk-copy">
            <strong>{title}</strong>
            <span id={`${menuId}-counts`} className="vocabulary-beanstalk-counts"><span>{v('eligible', { count: eligible })}</span><span>{v('waitingCards', { count: Math.max(0, pending - eligible) })}</span>{note.isEnabled === false && <span>{v('enable')}</span>}</span>
            <span id={`${menuId}-direction`} className="vocabulary-beanstalk-direction">{vocabularyDirectionLabel(note.vocabularyDirection, note)}</span>
          </span>
        </button>
        <div className="vocabulary-beanstalk-options" ref={optionsRef} onBlur={event => {
          const nextFocus = event.relatedTarget;
          // A touch tap may temporarily focus nothing or the app's tabindex
          // ancestor; let click finish before dismissing via the outside handler.
          if (nextFocus && !event.currentTarget.contains(nextFocus) && !nextFocus.contains?.(event.currentTarget)) setMenuOpen(false);
        }}>
          <button type="button" ref={triggerRef} className="vocabulary-beanstalk-menu-trigger" disabled={busy} aria-label={v('bookActions', { title })} aria-haspopup="menu" aria-expanded={menuOpen} aria-controls={menuOpen ? menuId : undefined} onClick={() => setMenuOpen(current => !current)}><MoreVertical size={20} aria-hidden="true" /></button>
          {menuOpen && <div className="vocabulary-beanstalk-menu" id={menuId} ref={menuRef} role="menu" onKeyDown={menuKeys}>
            <button type="button" role="menuitem" disabled={busy} onClick={() => act(onOpen)}>{v('details')}</button>
            <button type="button" role="menuitem" disabled={busy} onClick={() => act(onEdit)}>{v('edit')}</button>
            {total > 1 && onMoveMode && <button type="button" role="menuitem" disabled={busy} onClick={() => act(onMoveMode)}>{v(moving ? 'reorderDone' : 'reorder')}</button>}
            {onSetDirection && <VocabularyDirectionMenuItems direction={note.vocabularyDirection || 'q_to_a'} questionLocale={note.questionLocale} learningLocale={note.learningLocale} disabled={busy} onChange={value => act(() => onSetDirection(id, value))} />}
            <button type="button" role="menuitem" disabled={busy} onClick={() => act(onToggleEnabled)}>{v(note.isEnabled === false ? 'enable' : 'disable')}</button>
            <button type="button" role="menuitem" className="is-danger" disabled={busy} onClick={() => act(onDelete)}>{v('delete')}</button>
          </div>}
        </div>
      </div>
      {moving && <div className="vocabulary-beanstalk-move-controls">
        <button type="button" ref={element => registerControl(id, 'drag', element)} className="vocabulary-beanstalk-drag" {...attributes} {...listeners} disabled={busy} aria-label={`${v('reorder')}: ${title}`}><GripVertical size={19} aria-hidden="true" /></button>
        <button type="button" ref={element => registerControl(id, 'earlier', element)} disabled={busy || index === 0} aria-label={`${v('moveDownEarlier')}: ${title}`} onClick={() => onMove(index, index - 1, 'earlier')}><ArrowDown size={19} aria-hidden="true" /></button>
        <button type="button" ref={element => registerControl(id, 'later', element)} disabled={busy || index === total - 1} aria-label={`${v('moveUpLater')}: ${title}`} onClick={() => onMove(index, index + 1, 'later')}><ArrowUp size={19} aria-hidden="true" /></button>
      </div>}
    </div>
  </li>;
}

export default function VocabularyBeanstalk({ notes, total, progress, busy = false, moving = false, search = '', showEmptyIllustration = true, autoScrollToPosition = true, collectionHeader = null, collectionFeedback = null, scrollBottomBoundaryRef = null, onOpen, onEdit, onToggleEnabled, onSetDirection, onDelete, onMoveMode, onReorder }) {
  const rows = useMemo(() => getBeanstalkRows(notes), [notes]);
  const beanstalkRef = useRef(null);
  const collectionHeaderRef = useRef(null);
  const mapRef = useRef(null);
  const compactListRef = useRef(null);
  const hasScrolled = useRef(false);
  const pendingFocus = useRef(null);
  const pendingMapScroll = useRef(null);
  const controlRefs = useRef(new Map());
  const [mapTargetId, setMapTargetId] = useState(null);
  const [retainedId, setRetainedId] = useState(null);
  const [viewport, setViewport] = useState({ top: 0, height: window.innerHeight || 800, compactOffset: 0 });
  const [matchIndex, setMatchIndex] = useState(0);
  const query = search.trim().toLocaleLowerCase();
  const matches = useMemo(() => query ? rows.filter(row => `${vocabularyNoteTitle(row.note)} ${row.note.description || ''}`.toLocaleLowerCase().includes(query)) : [], [rows, query]);
  const globalY = getBeanstalkPosition(rows, total, progress);
  const section = useMemo(() => getBeanstalkSection(rows, total, progress, mapTargetId), [rows, total, progress, mapTargetId]);
  const { y, height } = section;
  const mapState = useRef(null);
  mapState.current = { rows, total, progress, section };
  const virtualCompact = !total && rows.length >= BEANSTALK_VIRTUAL_THRESHOLD;
  const compactRows = useMemo(() => rows.map(row => ({ ...row, top: row.index * BEANSTALK_ROW_HEIGHT, visualIndex: row.index })), [rows]);
  const order = rows.map(row => row.id).join('|');
  const hasCollectionHeader = total > 0 && Boolean(collectionHeader);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }), useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }));
  const registerControl = useCallback((id, control, element) => {
    const key = `${id}:${control}`;
    if (element) controlRefs.current.set(key, element); else controlRefs.current.delete(key);
  }, []);
  const compactOffset = useCallback(() => compactListRef.current && mapRef.current
    ? compactListRef.current.getBoundingClientRect().top - mapRef.current.getBoundingClientRect().top : 0, []);
  const scrollToY = useCallback((target, smooth = true) => {
    if (!mapRef.current) return;
    const height = window.innerHeight || 800;
    const offset = mapRef.current.getBoundingClientRect().top + window.scrollY;
    // Initial entry and My position share the visible band below the live
    // sticky header. CSS resolves the shell's navigation/safe-area clearance;
    // an optional explicit boundary still supports other map hosts with a dock.
    const header = collectionHeaderRef.current;
    const topInset = header ? header.getBoundingClientRect().height + Math.max(0, parseFloat(window.getComputedStyle(header).top) || 0) : 0;
    const dockTop = scrollBottomBoundaryRef?.current?.getBoundingClientRect().top;
    const bottomInset = beanstalkRef.current ? Math.max(0, parseFloat(window.getComputedStyle(beanstalkRef.current).scrollMarginBottom) || 0) : 0;
    const bottom = Number.isFinite(dockTop) ? Math.max(0, Math.min(height, dockTop)) : Math.max(0, height - bottomInset);
    // A very short/zoomed viewport can have no available band. Do not mistake
    // that for an absent dock and aim into its covered portion of the viewport.
    const anchor = Math.min(topInset, bottom) + Math.max(0, bottom - topInset) * 0.42;
    window.scrollTo({ top: Math.max(0, offset + target - anchor), behavior: smooth && !reducedMotion() ? 'smooth' : 'auto' });
    setViewport({ top: Math.max(0, target - anchor), height, compactOffset: compactOffset() });
  }, [compactOffset, scrollBottomBoundaryRef]);
  const scrollMapTo = useCallback((targetId, position = false, smooth = true) => {
    const current = mapState.current;
    const next = getBeanstalkSection(current.rows, current.total, current.progress, targetId);
    const row = next.rows.find(item => item.id === targetId);
    const target = position ? next.y : row && row.top + BEANSTALK_ROW_HEIGHT / 2;
    if (target == null) return;
    setMapTargetId(targetId);
    if (next.start !== current.section.start) pendingMapScroll.current = { start: next.start, target, smooth };
    else { pendingMapScroll.current = null; scrollToY(target, smooth); }
  }, [scrollToY]);
  useEffect(() => {
    const request = pendingMapScroll.current;
    if (!request || request.start !== section.start) return;
    pendingMapScroll.current = null;
    scrollToY(request.target, request.smooth);
  }, [section.start, scrollToY]);
  const scrollToRow = useCallback((row, smooth = true) => {
    if (!row) return;
    if (total) { scrollMapTo(row.id, false, smooth); return; }
    let target = row.top + BEANSTALK_ROW_HEIGHT / 2;
    if (!total) {
      const element = Array.from(mapRef.current?.querySelectorAll('[data-note-id]') || []).find(item => item.dataset.noteId === row.id);
      if (!mapRef.current) return;
      if (element) {
        const bounds = element.getBoundingClientRect();
        target = bounds.top - mapRef.current.getBoundingClientRect().top + bounds.height / 2;
      } else if (virtualCompact) target = compactOffset() + (row.index + 0.5) * BEANSTALK_ROW_HEIGHT;
      else return;
    }
    scrollToY(target, smooth);
  }, [total, virtualCompact, compactOffset, scrollToY, scrollMapTo]);
  useEffect(() => {
    const header = collectionHeaderRef.current;
    const root = beanstalkRef.current;
    if (!hasCollectionHeader || !header || !root) return undefined;
    const measure = () => root.style.setProperty('--beanstalk-header-height', `${Math.ceil(header.getBoundingClientRect().height)}px`);
    measure();
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measure);
    observer?.observe(header);
    window.addEventListener('resize', measure);
    return () => { observer?.disconnect(); window.removeEventListener('resize', measure); root.style.removeProperty('--beanstalk-header-height'); };
  }, [hasCollectionHeader]);
  useEffect(() => {
    if (!autoScrollToPosition || !total || y == null || hasScrolled.current) return;
    hasScrolled.current = true;
    scrollToY(y, false);
  }, [autoScrollToPosition, total, y, scrollToY]);
  useEffect(() => {
    let frame;
    const update = () => {
      if (frame) return;
      frame = requestAnimationFrame(() => { frame = null; if (mapRef.current) setViewport({ top: Math.max(0, -mapRef.current.getBoundingClientRect().top), height: window.innerHeight || 800, compactOffset: compactOffset() }); });
    };
    window.addEventListener('scroll', update, { passive: true }); window.addEventListener('resize', update);
    if (virtualCompact) update();
    return () => { window.removeEventListener('scroll', update); window.removeEventListener('resize', update); if (frame) cancelAnimationFrame(frame); };
  }, [compactOffset, virtualCompact]);
  useEffect(() => {
    setMatchIndex(0);
    if (matches.length) scrollToRow(matches[0]);
  }, [matches, scrollToRow]);
  const visible = total ? section.rows
    : virtualCompact ? getVisibleBeanstalkRows(compactRows, viewport.top - viewport.compactOffset + BEANSTALK_PADDING, viewport.height, retainedId) : rows;
  const visualRows = total ? [...rows].reverse() : rows;
  useEffect(() => {
    const request = pendingFocus.current;
    if (!request) return;
    if (busy) { request.sawBusy = true; return; }
    if (request.oldOrder && request.oldOrder === order && !request.sawBusy) return;
    const row = rows.find(item => item.id === request.id);
    if (!row) { pendingFocus.current = null; return; }
    const rowElement = Array.from(mapRef.current?.querySelectorAll('[data-note-id]') || []).find(element => element.dataset.noteId === request.id);
    const preferred = request.control && controlRefs.current.get(`${request.id}:${request.control}`);
    const drag = request.control && controlRefs.current.get(`${request.id}:drag`);
    const target = preferred && !preferred.disabled ? preferred : drag && !drag.disabled ? drag : request.last ? focusable(rowElement).at(-1) : focusable(rowElement)[0];
    if (target) {
      if (request.oldOrder) scrollToRow(row);
      target.focus({ preventScroll: true }); pendingFocus.current = null;
    }
  }, [rows, viewport, busy, order, scrollToRow, section.start]);
  const goToRow = (row, options = {}) => {
    if (!row) return;
    pendingFocus.current = { id: row.id, ...options };
    setRetainedId(row.id);
    scrollToRow(row);
  };
  const navigateRow = (id, delta) => goToRow(visualRows[visualRows.findIndex(row => row.id === id) + delta]);
  const move = (from, to, control = 'drag') => {
    if (busy || from === to || to < 0 || to >= rows.length) return;
    pendingFocus.current = { id: rows[from].id, control, oldOrder: order };
    setRetainedId(rows[from].id);
    if (total) setMapTargetId(rows[from].id);
    onReorder(from, to, control);
  };
  const handleTab = event => {
    if (event.key !== 'Tab' || event.target.closest('[role="menu"]')) return;
    const rowElement = event.target.closest('[data-note-id]');
    if (!rowElement) return;
    const buttons = focusable(rowElement);
    const edge = event.shiftKey ? buttons[0] : buttons.at(-1);
    if (event.target !== edge) return;
    const row = visualRows[visualRows.findIndex(item => item.id === rowElement.dataset.noteId) + (event.shiftKey ? -1 : 1)];
    if (row) { event.preventDefault(); goToRow(row, { last: event.shiftKey }); }
  };
  const nextMatch = delta => {
    const next = (matchIndex + delta + matches.length) % matches.length;
    setMatchIndex(next); scrollToRow(matches[next]);
  };
  const masteredCount = notes.reduce((count, note) => count + Math.max(0, Number(note.completedCount) || 0) + Math.max(0, Number(note.alreadyKnownCount) || 0), 0);
  const leafProps = { total: rows.length, busy, moving, onOpen, onEdit, onToggleEnabled, onSetDirection, onDelete, onMoveMode, onMove: move, registerControl, onNavigate: navigateRow };
  return <div ref={beanstalkRef} className={`vocabulary-beanstalk${hasCollectionHeader ? ' has-collection-header' : ''}${total > 0 && !section.hasRoot ? ' is-upper-section' : ''}`} style={{ '--beanstalk-tile-size': `${BEANSTALK_TILE_SIZE}px`, '--beanstalk-row-height': `${BEANSTALK_ROW_HEIGHT}px` }} onFocusCapture={event => { const row = event.target.closest('[data-note-id]'); if (row) setRetainedId(row.dataset.noteId); }} onKeyDownCapture={handleTab}>
    {hasCollectionHeader && <div ref={collectionHeaderRef} className="vocabulary-beanstalk-collection-header" data-testid="vocabulary-beanstalk-collection-header">{collectionHeader}</div>}
    {total > 0 && collectionFeedback}
    {(total > 0 || query) && <div className="vocabulary-beanstalk-navigation">
      {total > 0 && globalY != null && <button type="button" className="vocabulary-beanstalk-position-button" onClick={() => { pendingFocus.current = null; scrollMapTo(null, true); }}><Crosshair size={18} aria-hidden="true" />{v('myPosition')}</button>}
      {total > 0 && (section.start > 0 || section.end < rows.length) && <nav className="vocabulary-beanstalk-floor-navigation" aria-label={v('mapSections')}>
        <button type="button" aria-label={v('lowerFloors')} disabled={busy || section.start === 0} onClick={() => goToRow(rows[section.start - 1])}><ChevronDown size={19} aria-hidden="true" /></button>
        <span role="status">{v('floorRange', { start: section.start + 1, end: section.end })}</span>
        <button type="button" aria-label={v('higherFloors')} disabled={busy || section.end === rows.length} onClick={() => goToRow(rows[section.end])}><ChevronUp size={19} aria-hidden="true" /></button>
      </nav>}
      {query && (matches.length ? <div className="vocabulary-beanstalk-search-navigation"><span role="status">{v('matchCount', { current: Math.min(matchIndex + 1, matches.length), total: matches.length })}</span><button type="button" aria-label={v('previousMatch')} onClick={() => nextMatch(-1)}><ChevronUp size={19} aria-hidden="true" /></button><button type="button" aria-label={v('nextMatch')} onClick={() => nextMatch(1)}><ChevronDown size={19} aria-hidden="true" /></button></div> : <p role="status">{v('noResults')}</p>)}
    </div>}
    {!total ? <div ref={mapRef} className="vocabulary-beanstalk-empty" data-testid="vocabulary-beanstalk-empty-region">{showEmptyIllustration && <VocabularySproutScene />}{notes.length > 0 && <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={({ active, over }) => { if (over && moving) move(rows.findIndex(row => row.id === active.id), rows.findIndex(row => row.id === over.id)); }}><SortableContext items={rows.map(row => row.id)} strategy={verticalListSortingStrategy}><ul ref={compactListRef} className={`vocabulary-beanstalk-empty-books${virtualCompact ? ' is-virtualized' : ''}`} data-testid="vocabulary-empty-book-list" style={virtualCompact ? { height: rows.length * BEANSTALK_ROW_HEIGHT } : undefined}>{visible.map(row => <BookLeaf key={row.id} row={row} compact virtualCompact={virtualCompact} isMatch={matches.some(match => match.id === row.id)} {...leafProps} />)}</ul></SortableContext></DndContext>}</div>
      : <div ref={mapRef} className={`vocabulary-beanstalk-map${section.hasRoot ? '' : ' is-upper-section'}`} data-testid="vocabulary-beanstalk-map" style={{ height }}>
        <BeanstalkArtwork rows={visible} height={height} hasRoot={section.hasRoot} hasTop={section.hasTop} />
        {section.hasTop && <BeanstalkCrownStar />}
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={({ active, over }) => { if (over && moving) move(rows.findIndex(row => row.id === active.id), rows.findIndex(row => row.id === over.id)); }}>
          <SortableContext items={[...section.rows].reverse().map(row => row.id)} strategy={verticalListSortingStrategy}>
            <ol className="vocabulary-beanstalk-books" reversed start={section.end} aria-label={v('mapLabel')}>{[...visible].reverse().map(row => <BookLeaf key={row.id} row={row} isMatch={matches.some(match => match.id === row.id)} {...leafProps} />)}</ol>
          </SortableContext>
        </DndContext>
        {y != null && <div className="vocabulary-beanstalk-climber" data-testid="vocabulary-climber-position" style={{ top: y }}>
          <img src={climbingPlayer} alt="" aria-hidden="true" width={256} height={256} draggable={false} data-testid="vocabulary-climber" />
          {masteredCount > 0 && <span>{v('masteredWordCount', { count: masteredCount })}</span>}
        </div>}
        {section.hasRoot && <span className="vocabulary-beanstalk-origin" aria-hidden="true">0</span>}
      </div>}
  </div>;
}
